import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from './AuthProvider';
import MyBookings from './MyBookings';
import '../styles/Header.css';
import logo from '../welegallogo.png';

interface HeaderProps {
  onOpenLogin: () => void;
  onOpenSignup: () => void;
}

const Header: React.FC<HeaderProps> = ({ onOpenLogin, onOpenSignup }) => {
  const { user, logout } = useContext(AuthContext) || { user: null, logout: () => {} };
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMyBookings, setShowMyBookings] = useState(false);
  const userMenuRef = useRef<HTMLLIElement>(null);
  const navigate = useNavigate();

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const handleMyBookingsClick = () => {
    setShowMyBookings(true);
    setShowUserMenu(false);
  };

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(event.target as Node)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showUserMenu]);

  const handleMenuClick = (section: string) => {
    setActiveSection(section);
    setIsMenuOpen(false);
    
    // Navigate to home page first, then scroll to section
    if (window.location.pathname !== '/') {
      navigate('/');
      setTimeout(() => {
        const element = document.querySelector(`#${section}`);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      const element = document.querySelector(`#${section}`);
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <>
    <nav className="navbar">
      <div className="nav-container">
        <div className="nav-logo">
          <img src={logo} alt="WeLegal" className="logo-img" />
        </div>
        <ul className={`nav-menu ${isMenuOpen ? 'active' : ''}`}>
          <li className="nav-item">
            <a href="#home" className={`nav-link ${activeSection === 'home' ? 'active' : ''}`} onClick={() => handleMenuClick('home')}>Home</a>
          </li>
          <li className="nav-item">
            <a href="#services" className={`nav-link ${activeSection === 'services' ? 'active' : ''}`} onClick={() => handleMenuClick('services')}>Services</a>
          </li>
          <li className="nav-item">
            <a href="#about" className={`nav-link ${activeSection === 'about' ? 'active' : ''}`} onClick={() => handleMenuClick('about')}>About</a>
          </li>
          <li className="nav-item">
            <a href="#contact" className={`nav-link ${activeSection === 'contact' ? 'active' : ''}`} onClick={() => handleMenuClick('contact')}>Contact</a>
          </li>
          {!user ? (
            <li className="nav-item">
              <button onClick={onOpenSignup} className="nav-link signup-btn">Sign Up</button>
            </li>
          ) : (
            <li className="nav-item user-menu" ref={userMenuRef}>
              <div className="user-profile" onClick={toggleUserMenu}>
                <div className="user-avatar">
                  <i className="fas fa-user"></i>
                </div>
                <span className="user-name">{user.firstName}</span>
                <i className={`fas fa-chevron-down ${showUserMenu ? 'rotate' : ''}`}></i>
              </div>
              {showUserMenu && (
                <div className="user-dropdown">
                  <div className="user-info">
                    <span className="user-full-name">{user.firstName} {user.lastName}</span>
                    <span className="user-email">{user.email}</span>
                  </div>
                  <hr className="dropdown-divider" />
                  <button onClick={() => navigate('/profile')} className="dropdown-item">
                    <i className="fas fa-user"></i>
                    My Profile
                  </button>
                  <button onClick={handleMyBookingsClick} className="dropdown-item">
                    <i className="fas fa-calendar-alt"></i>
                    My Bookings
                  </button>
                  {user.role === 'admin' && (
                    <button onClick={() => navigate('/admin')} className="dropdown-item">
                      <i className="fas fa-cog"></i>
                      Admin Dashboard
                    </button>
                  )}
                  <hr className="dropdown-divider" />
                  <button onClick={handleLogout} className="dropdown-item logout">
                    <i className="fas fa-sign-out-alt"></i>
                    Logout
                  </button>
                </div>
              )}
            </li>
          )}
        </ul>
        <div className={`hamburger ${isMenuOpen ? 'active' : ''}`} onClick={toggleMenu}>
          <span className="bar"></span>
          <span className="bar"></span>
          <span className="bar"></span>
        </div>
      </div>
    </nav>
    
    {/* My Bookings Modal */}
    <MyBookings 
      isOpen={showMyBookings} 
      onClose={() => setShowMyBookings(false)} 
    />
    </>
  );
};

export default Header;